import { useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  AlertDescription,
  Textarea,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';

import { apiGetCommercialFormData } from 'api';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useCommercialAutoSigningEnabled } from 'hooks/useCommercialAutoSigningEnabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import {
  initializeForm,
  resetForm,
  setLineOfBusiness,
  setAdditionalBrokerNote,
  setNotesToUnderwriter,
  setApplicantAddressField,
} from 'reducers/commercialFormReducer';
import {
  WIP_STATUS_BINDER_PENDING_UPLOAD,
  WIP_STATUS_DECLINED,
  WIP_STATUS_UW_DECLINED,
  ROLE_ADMIN,
  WIP_STATUS_BINDER_UPLOADED,
} from 'variables/constants';
import { AuthContext } from 'contexts/AuthContext';
import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import CommercialPolicyInformation from './sections/CommercialPolicyInformation';
import CommercialCoApplicants from './sections/CommercialCoApplicants';
import CommercialBilling from './sections/CommercialBilling/CommercialBilling';
import CommercialMethodOfPayment from './sections/CommercialMethodOfPayment/CommercialMethodOfPayment';
import CommercialDeclarationUpload from './sections/CommercialDeclarationUpload';
import CommercialQuoteUpload from './sections/CommercialQuoteUpload';
import CommercialChecklist from './sections/CommercialChecklist';
import CommercialApplicationUpload from './sections/CommercialApplicationUpload/CommercialApplicationUpload';
import CommercialSubmit from './sections/CommercialSubmit';
import CommercialMVRUpload from './sections/CommercialMVRUpload/CommercialMVRUpload';
import CommercialDashReport from './sections/CommercialDashReport/CommercialDashReport';
import DisclosureAndAttestation from 'views/shared/DisclosureAndAttestation';
import Card from 'components/card/Card';
import { APICommercialForm } from 'types/api.types';

const CommercialFormView = () => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const isDataSet = useAppSelector((state) => state.commercialForm.isDataSet);
  const wipStatus = useAppSelector((state) => state.commercialForm.wipStatus);
  const additionalBrokerNote = useAppSelector(
    (state) => state.commercialForm.data.additional_broker_note,
  );
  const notesToUnderwriter = useAppSelector(
    (state) => state.commercialForm.data.notes_to_underwriter || '',
  );
  const insuranceCompany = useAppSelector(
    (state) => state.commercialForm.data.insurance_company,
  );
  const signing = useAppSelector((state) => state.commercialForm.signing);
  const isJiraWipStatusesEnabled = useFeatureFlag(
    'enable-jira-submission-wip-statuses',
  );
  const isDeclined =
    isJiraWipStatusesEnabled &&
    (wipStatus === WIP_STATUS_DECLINED || wipStatus === WIP_STATUS_UW_DECLINED);

  const isLocked =
    wipStatus === WIP_STATUS_BINDER_PENDING_UPLOAD || isDeclined || !!signing;
  const isAdmin = user?.role === ROLE_ADMIN;
  const canEditPremium = isAdmin && isLocked;
  const isCommercialAutoSigningEnabled = useCommercialAutoSigningEnabled();

  const { data: initialData, isFetching } = useQuery({
    queryKey: ['commercial_form', uuid],
    queryFn: () => apiGetCommercialFormData(uuid!),
    refetchOnWindowFocus: false,
  }) as {
    error: any;
    data: APICommercialForm;
    isFetching: boolean;
  };

  useEffect(() => {
    dispatch(resetForm());
  }, [uuid, dispatch]);

  useEffect(() => {
    if (isDataSet || isFetching) return;

    // Initialize commercial form with API data or empty state
    if (initialData) {
      dispatch(initializeForm(initialData));

      // sync the account_holders based on the is_same_as_applicant field since there can be changes during init
      if (initialData.applicant_address && initialData.account_holders) {
        dispatch(
          setApplicantAddressField({
            field: 'province',
            value: initialData.applicant_address.province || '',
          }),
        );
      }

      if (initialData.line_of_business) {
        dispatch(
          setLineOfBusiness(
            initialData.line_of_business as 'Property & Casualty' | 'Auto',
          ),
        );
      }
    } else if (!isFetching) {
      // No initial data, initialize with empty state
      dispatch(initializeForm({}));
    }
  }, [initialData, uuid, isDataSet, isFetching, dispatch]);

  if (!isDataSet && isFetching) {
    return null;
  }

  return (
    <ApplicationFormContext.Provider value={{ validations: [] }}>
      {isJiraWipStatusesEnabled &&
        isLocked &&
        wipStatus === WIP_STATUS_BINDER_UPLOADED && (
          <Alert status="success" borderRadius="md" mb="20px">
            <AlertIcon />
            <AlertDescription>
              <strong>
                This submission has been approved by the market. No further
                changes can be made.
              </strong>
            </AlertDescription>
          </Alert>
        )}
      {isDeclined && wipStatus === WIP_STATUS_DECLINED && (
        <Alert status="error" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This submission has been declined. No further changes can be made.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      {isDeclined && wipStatus === WIP_STATUS_UW_DECLINED && (
        <Alert status="error" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This submission has been declined by the underwriter. No further
              changes can be made.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      {isLocked && !isDeclined && isAdmin && (
        <Alert status="warning" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This form has been submitted. As an admin, you may only edit the
              premium field. All other fields are locked.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      {isLocked && !isDeclined && !isAdmin && (
        <Alert status="info" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This form has been completed and submitted. No further changes can
              be made.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      <CommercialPolicyInformation
        isLocked={isLocked}
        canEditPremium={canEditPremium}
      />
      {isCommercialAutoSigningEnabled && (
        <CommercialCoApplicants isLocked={isLocked} />
      )}
      {isCommercialAutoSigningEnabled ? (
        <CommercialMethodOfPayment isLocked={isLocked} />
      ) : (
        <CommercialBilling isLocked={isLocked} />
      )}
      <CommercialChecklist isLocked={isLocked} />
      {isCommercialAutoSigningEnabled && (
        <CommercialApplicationUpload isLocked={isLocked} />
      )}
      {!isCommercialAutoSigningEnabled && (
        <>
          <CommercialDeclarationUpload isLocked={isLocked} />
          <CommercialQuoteUpload isLocked={isLocked} />
        </>
      )}

      {/* Show MVR and DASH sections for Aviva Elite (similar to specialty LOA form) */}
      {insuranceCompany === 'Aviva Elite' && (
        <>
          <CommercialMVRUpload isLocked={isLocked} />
          <CommercialDashReport isLocked={isLocked} />
        </>
      )}

      <DisclosureAndAttestation
        formStateKey="commercialForm"
        isLocked={isLocked}
      />
      <Card mb="20px">
        <FormControl>
          <FormLabel fontWeight="bold" fontSize="lg" mb={4}>
            Additional Broker Note
          </FormLabel>
          <Textarea
            value={additionalBrokerNote || ''}
            onChange={(e) => dispatch(setAdditionalBrokerNote(e.target.value))}
            placeholder="Enter any additional notes if needed"
            isDisabled={isLocked}
            rows={4}
          />
        </FormControl>
      </Card>
      <Card mb="20px">
        <FormControl>
          <FormLabel fontWeight="bold" fontSize="lg" mb={4}>
            Notes for BIG Underwriting
          </FormLabel>
          <Textarea
            value={notesToUnderwriter}
            onChange={(e) => dispatch(setNotesToUnderwriter(e.target.value))}
            placeholder="These notes will only be visible in the Jira ticket and will not appear on any client-facing documents"
            isDisabled={isLocked}
            rows={4}
          />
        </FormControl>
      </Card>
      <CommercialSubmit isLocked={isLocked} canEditPremium={canEditPremium} />
    </ApplicationFormContext.Provider>
  );
};

export default CommercialFormView;
